import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { devices as builtInDevices } from "../data/devices";
import { STORAGE_KEYS } from "../constants/storageKeys";
import { createCustomDevice } from "../utils/deviceUtils";
import { sanitizeOrientationMap } from "../utils/sessionUtils";
import { useLocalStorage } from "./useLocalStorage";

const isStringArray = (value) => Array.isArray(value) && value.every((v) => typeof v === "string");
const isPlainObject = (value) => Boolean(value) && typeof value === "object" && !Array.isArray(value);
const isCustomDevice = (d) =>
  isPlainObject(d) &&
  typeof d.id === "string" &&
  typeof d.name === "string" &&
  typeof d.brand === "string" &&
  typeof d.category === "string" &&
  Number.isFinite(d.width) &&
  Number.isFinite(d.height);
// Drop individual malformed entries rather than discarding every custom device.
const sanitizeCustomDevices = (value) => value.filter(isCustomDevice);

const withoutKey = (map, id) => {
  if (!(id in map)) return map;
  const next = { ...map };
  delete next[id];
  return next;
};

/**
 * Owns device catalog state: built-in + custom devices, favorites,
 * selection, per-device orientation, and recently-used tracking.
 * Favorites, custom devices, selection, and orientation persist to localStorage.
 */
export function useDeviceSelection() {
  const [customDevices, setCustomDevices] = useLocalStorage(
    STORAGE_KEYS.CUSTOM_DEVICES,
    [],
    Array.isArray,
    sanitizeCustomDevices
  );
  const [favoriteIds, setFavoriteIds] = useLocalStorage(
    STORAGE_KEYS.FAVORITE_DEVICE_IDS,
    [],
    isStringArray
  );
  const [selectedDeviceIds, setSelectedDeviceIds] = useLocalStorage(
    STORAGE_KEYS.LAST_SELECTED_DEVICE_IDS,
    [],
    isStringArray
  );
  const [orientationByDevice, setOrientationByDevice] = useLocalStorage(
    STORAGE_KEYS.ORIENTATION_BY_DEVICE,
    {},
    isPlainObject,
    sanitizeOrientationMap
  );
  const [recentIds, setRecentIds] = useState([]);

  // Latest selection, readable from stable callbacks without re-creating them
  // (DeviceRow is memoized on `onToggle`).
  const selectedIdsRef = useRef(selectedDeviceIds);
  useEffect(() => {
    selectedIdsRef.current = selectedDeviceIds;
  }, [selectedDeviceIds]);

  const allDevices = useMemo(() => [...builtInDevices, ...customDevices], [customDevices]);
  const devicesById = useMemo(() => {
    const map = new Map();
    allDevices.forEach((d) => map.set(d.id, d));
    return map;
  }, [allDevices]);

  const selectedDevices = useMemo(
    () => selectedDeviceIds.map((id) => devicesById.get(id)).filter(Boolean),
    [selectedDeviceIds, devicesById]
  );

  const removeDevice = useCallback(
    (deviceId) => {
      setSelectedDeviceIds((prev) => prev.filter((id) => id !== deviceId));
      setOrientationByDevice((prev) => withoutKey(prev, deviceId));
    },
    [setSelectedDeviceIds, setOrientationByDevice]
  );

  const toggleDevice = useCallback(
    (deviceId) => {
      if (selectedIdsRef.current.includes(deviceId)) {
        removeDevice(deviceId);
        return;
      }
      setSelectedDeviceIds((prev) => (prev.includes(deviceId) ? prev : [...prev, deviceId]));
      setRecentIds((prev) => [deviceId, ...prev.filter((id) => id !== deviceId)].slice(0, 8));
    },
    [removeDevice, setSelectedDeviceIds]
  );

  const selectAll = useCallback(
    (visibleIds) => {
      setSelectedDeviceIds((prev) => Array.from(new Set([...prev, ...visibleIds])));
    },
    [setSelectedDeviceIds]
  );

  const clearSelection = useCallback(() => {
    setSelectedDeviceIds([]);
    setOrientationByDevice({});
  }, [setSelectedDeviceIds, setOrientationByDevice]);

  const toggleFavorite = useCallback(
    (deviceId) => {
      setFavoriteIds((prev) =>
        prev.includes(deviceId) ? prev.filter((id) => id !== deviceId) : [...prev, deviceId]
      );
    },
    [setFavoriteIds]
  );

  const toggleOrientation = useCallback(
    (deviceId) => {
      setOrientationByDevice((prev) => ({ ...prev, [deviceId]: !prev[deviceId] }));
    },
    [setOrientationByDevice]
  );

  const rotateAll = useCallback(() => {
    setOrientationByDevice((prev) => {
      const next = { ...prev };
      selectedDevices.forEach((device) => {
        if (device.supportsRotation) next[device.id] = !prev[device.id];
      });
      return next;
    });
  }, [selectedDevices, setOrientationByDevice]);

  const addCustomDevice = useCallback(
    ({ name, width, height }) => {
      const device = createCustomDevice({ name, width, height }, allDevices);
      setCustomDevices((prev) => [...prev, device]);
      return device;
    },
    [allDevices, setCustomDevices]
  );

  const updateCustomDevice = useCallback(
    (deviceId, updates) => {
      setCustomDevices((prev) =>
        prev.map((d) => (d.id === deviceId ? { ...d, ...updates } : d))
      );
    },
    [setCustomDevices]
  );

  const deleteCustomDevice = useCallback(
    (deviceId) => {
      setCustomDevices((prev) => prev.filter((d) => d.id !== deviceId));
      setSelectedDeviceIds((prev) => prev.filter((id) => id !== deviceId));
      setFavoriteIds((prev) => prev.filter((id) => id !== deviceId));
      setOrientationByDevice((prev) => withoutKey(prev, deviceId));
      setRecentIds((prev) => prev.filter((id) => id !== deviceId));
    },
    [setCustomDevices, setSelectedDeviceIds, setFavoriteIds, setOrientationByDevice]
  );

  const applySelection = useCallback(
    ({ selectedDeviceIds: ids, orientationByDevice: orientation }) => {
      const nextIds = isStringArray(ids) ? ids : [];
      const cleanOrientation = sanitizeOrientationMap(orientation);
      // Only keep rotation for devices that are actually part of the selection.
      const nextOrientation = nextIds.reduce((map, id) => {
        if (cleanOrientation[id]) map[id] = true;
        return map;
      }, {});
      setSelectedDeviceIds(nextIds);
      setOrientationByDevice(nextOrientation);
    },
    [setSelectedDeviceIds, setOrientationByDevice]
  );

  return {
    allDevices,
    devicesById,
    customDevices,
    favoriteIds,
    selectedDeviceIds,
    selectedDevices,
    orientationByDevice,
    recentIds,
    toggleDevice,
    removeDevice,
    selectAll,
    clearSelection,
    toggleFavorite,
    toggleOrientation,
    rotateAll,
    addCustomDevice,
    updateCustomDevice,
    deleteCustomDevice,
    applySelection,
  };
}
